(() => {
  const asNumber = (value, fallback = 1) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const fillTemplate = (template, values) => Object.entries(values).reduce(
    (result, [key, value]) => result.split(`__${key}__`).join(String(value)),
    template || ''
  );

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'CLP';
    const locale = document.documentElement.lang || 'es-CL';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: currency === 'CLP' ? 0 : 2,
        maximumFractionDigits: currency === 'CLP' ? 0 : 2,
      }).format(cents / 100);
    } catch (error) {
      return `$${Math.round(cents / 100).toLocaleString('es-CL')}`;
    }
  };

  const multibuyTierForQuantity = (selector, quantity) => {
    const firstQuantity = asNumber(selector.dataset.czFirstQuantity, 3);
    const firstDiscount = asNumber(selector.dataset.czFirstDiscount, 12);
    const secondQuantity = asNumber(selector.dataset.czSecondQuantity, 5);
    const secondDiscount = asNumber(selector.dataset.czSecondDiscount, 18);
    if (quantity >= secondQuantity) return { quantity: secondQuantity, discount: secondDiscount };
    if (quantity >= firstQuantity) return { quantity: firstQuantity, discount: firstDiscount };
    return { quantity: 1, discount: 0 };
  };

  const syncMultibuy = (root, requestedQuantity) => {
    const selector = root?.querySelector('[data-cz-multibuy]');
    const quantityInput = root?.querySelector('[data-cz-quantity]');
    if (!selector || !quantityInput) return;

    const quantity = Math.max(1, asNumber(requestedQuantity ?? quantityInput.value, 1));
    quantityInput.value = String(quantity);
    const activeTier = multibuyTierForQuantity(selector, quantity);
    const priceCents = Math.max(0, asNumber(selector.dataset.czPriceCents, 0));
    const available = root.dataset.czVariantAvailable !== 'false';

    selector.querySelectorAll('[data-cz-multibuy-tier]').forEach((button) => {
      const tierQuantity = asNumber(button.dataset.quantity, 1);
      const tierDiscount = Math.max(0, asNumber(button.dataset.discount, 0));
      const selected = tierQuantity === activeTier.quantity;
      const unitPrice = Math.round(priceCents * (100 - tierDiscount) / 100);
      const totalPrice = Math.round(priceCents * tierQuantity * (100 - tierDiscount) / 100);
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-checked', String(selected));
      button.disabled = !available;
      const totalNode = button.querySelector('[data-cz-multibuy-total]');
      const eachNode = button.querySelector('[data-cz-multibuy-each]');
      if (totalNode) totalNode.textContent = formatMoney(totalPrice);
      if (eachNode) eachNode.textContent = `${formatMoney(unitPrice)} c/u`;
    });

    const status = selector.querySelector('[data-cz-multibuy-status]');
    if (status) {
      status.textContent = activeTier.discount > 0
        ? fillTemplate(selector.dataset.czStatusTier, { quantity, discount: activeTier.discount })
        : selector.dataset.czStatusRegular || '';
    }

    root.querySelectorAll('[data-cz-add-button]').forEach((button) => {
      button.disabled = !available;
      if (!available) {
        button.textContent = 'Agotado';
        return;
      }
      if (activeTier.discount <= 0) {
        button.textContent = selector.dataset.czAddRegular || 'Agregar al carro';
        return;
      }
      const template = button.matches('[data-cz-sticky-add]')
        ? selector.dataset.czStickyTier
        : selector.dataset.czAddTier;
      button.textContent = fillTemplate(template, { quantity, discount: activeTier.discount });
    });
  };

  const setupMultibuy = (scope = document) => {
    const roots = scope.matches?.('[data-cz-product-root]')
      ? [scope]
      : scope.querySelectorAll('[data-cz-product-root]');
    roots.forEach((root) => syncMultibuy(root));
  };

  const syncFilterLock = () => {
    const open = Boolean(document.querySelector('.cz-mobile-filters[open]'));
    document.body.classList.toggle('cz-filters-open', open);
  };

  const closeFilters = (details) => {
    if (!details) return;
    details.removeAttribute('open');
    syncFilterLock();
  };

  const cardDetailsAreMobile = () => window.matchMedia('(max-width: 749px)').matches;

  const syncCardDetailsLock = () => {
    const openDrawer = cardDetailsAreMobile() && Boolean(document.querySelector('[data-cz-card-details][open]'));
    document.body.classList.toggle('cz-card-details-open', openDrawer);
  };

  const closeCardDetails = (details, restoreFocus = false) => {
    if (!details) return;
    details.removeAttribute('open');
    syncCardDetailsLock();
    if (restoreFocus) details.querySelector('summary')?.focus();
  };

  const syncProductImage = (root, imageId) => {
    if (!root || !imageId) return;
    const thumb = root.querySelector(`[data-cz-product-thumb][data-cz-image-id="${imageId}"]`);
    if (!thumb) return;
    thumb.click();
  };

  const galleryItems = (gallery) => Array.from(gallery.querySelectorAll('[data-cz-product-media]'));
  const galleryThumbnails = (gallery) => Array.from(gallery.querySelectorAll('[data-cz-product-thumb]'));

  const selectGalleryIndex = (gallery, requestedIndex) => {
    const items = galleryItems(gallery);
    if (!items.length) return;

    const index = ((requestedIndex % items.length) + items.length) % items.length;
    const thumbnails = galleryThumbnails(gallery);

    items.forEach((item, itemIndex) => {
      const selected = itemIndex === index;
      item.classList.toggle('is-selected', selected);
      item.hidden = !selected;
    });

    thumbnails.forEach((thumb, thumbIndex) => {
      const selected = thumbIndex === index;
      thumb.classList.toggle('is-active', selected);
      thumb.setAttribute('aria-current', String(selected));
    });

    gallery.dataset.czGalleryIndex = String(index);

    const counter = gallery.querySelector('[data-cz-gallery-current]');
    if (counter) counter.textContent = String(index + 1);

    const status = gallery.querySelector('[data-cz-gallery-status]');
    if (status) status.textContent = `Imagen ${index + 1} de ${items.length}`;

    const selectedThumb = thumbnails[index];
    if (selectedThumb) {
      selectedThumb.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  };

  const selectGalleryThumbnail = (thumb) => {
    const gallery = thumb?.closest('[data-cz-gallery]');
    if (!gallery) return;

    const index = galleryThumbnails(gallery).indexOf(thumb);
    if (index >= 0) selectGalleryIndex(gallery, index);
  };

  const moveGallery = (gallery, direction) => {
    const currentIndex = asNumber(gallery.dataset.czGalleryIndex, 0);
    selectGalleryIndex(gallery, currentIndex + direction);
  };

  const setupProductGalleries = (scope = document) => {
    scope.querySelectorAll('[data-cz-gallery]').forEach((gallery) => {
      if (gallery.dataset.czGalleryInitialized === 'true') return;
      gallery.dataset.czGalleryInitialized = 'true';

      const items = galleryItems(gallery);
      const initialIndex = Math.max(0, items.findIndex((item) => item.classList.contains('is-selected')));
      selectGalleryIndex(gallery, initialIndex);

      gallery.querySelectorAll('[data-cz-product-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => selectGalleryThumbnail(thumb));
      });

      gallery.querySelector('[data-cz-gallery-previous]')?.addEventListener('click', () => moveGallery(gallery, -1));
      gallery.querySelector('[data-cz-gallery-next]')?.addEventListener('click', () => moveGallery(gallery, 1));

      const surface = gallery.querySelector('[data-cz-gallery-surface]');
      if (!surface || items.length < 2) return;

      let gesture = null;

      surface.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' || event.target.closest('button')) return;
        gesture = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
      });

      surface.addEventListener('pointerup', (event) => {
        if (!gesture || event.pointerId !== gesture.pointerId) return;

        const distanceX = event.clientX - gesture.x;
        const distanceY = event.clientY - gesture.y;
        gesture = null;

        if (Math.abs(distanceX) < 42 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.15) return;
        moveGallery(gallery, distanceX < 0 ? 1 : -1);
      });

      surface.addEventListener('pointercancel', () => {
        gesture = null;
      });
    });
  };

  const updateProductVariant = (variantSelect) => {
    const root = variantSelect.closest('[data-cz-product-root]');
    const option = variantSelect.selectedOptions[0];
    if (!root || !option) return;

    const available = option.dataset.available === 'true';
    const discount = asNumber(option.dataset.discount, 0);
    const priceText = option.dataset.price || '';
    const skuText = option.dataset.sku || '';
    root.dataset.czVariantAvailable = String(available);

    root.querySelectorAll('[data-cz-product-price], [data-cz-sticky-price]').forEach((node) => {
      node.textContent = priceText;
    });

    const compare = root.querySelector('[data-cz-product-compare]');
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }

    const sku = root.querySelector('[data-cz-product-sku]');
    if (sku) {
      sku.textContent = skuText ? `SKU: ${skuText}` : '';
      sku.hidden = !skuText;
    }

    root.querySelectorAll('[data-cz-discount-badge]').forEach((badge) => {
      badge.textContent = discount > 0 ? `−${discount}%` : '';
      badge.hidden = discount <= 0;
    });

    const availability = root.querySelector('[data-cz-availability]');
    if (availability) {
      availability.textContent = available ? availability.dataset.availableLabel : availability.dataset.unavailableLabel;
      availability.classList.toggle('is-out', !available);
    }

    const imageStockBadge = root.querySelector('[data-cz-image-stock-badge]');
    if (imageStockBadge) imageStockBadge.hidden = available;

    const multibuy = root.querySelector('[data-cz-multibuy]');
    if (multibuy) {
      multibuy.dataset.czPriceCents = option.dataset.priceCents || '0';
      multibuy.querySelectorAll('[data-cz-multibuy-tier]').forEach((button) => {
        button.disabled = !available;
      });
      syncMultibuy(root);
    } else {
      root.querySelectorAll('[data-cz-add-button]').forEach((button) => {
        button.disabled = !available;
        button.textContent = available ? 'Agregar al carro' : 'Agotado';
      });
    }

    syncProductImage(root, option.dataset.imageId);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    window.history.replaceState({}, '', url);
  };

  const setupStickyBuy = (scope = document) => {
    scope.querySelectorAll('[data-cz-sticky-buy]').forEach((sticky) => {
      if (sticky.dataset.czInitialized === 'true') return;
      const root = sticky.closest('[data-cz-product-root]');
      const target = root?.querySelector('[data-cz-main-buy-row]');
      const button = sticky.querySelector('[data-cz-sticky-add]');
      if (!target || !button || !('IntersectionObserver' in window)) return;

      sticky.dataset.czInitialized = 'true';
      const setVisible = (visible) => {
        const allowed = visible && window.innerWidth <= 749;
        sticky.classList.toggle('is-visible', allowed);
        sticky.setAttribute('aria-hidden', String(!allowed));
        button.tabIndex = allowed ? 0 : -1;
      };

      const observer = new IntersectionObserver(([entry]) => {
        const passedMainButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        setVisible(passedMainButton);
      }, { threshold: 0.15 });
      observer.observe(target);
    });
  };

  document.addEventListener('toggle', (event) => {
    const cardDetails = event.target.closest?.('[data-cz-card-details]');
    if (cardDetails) {
      if (cardDetails.open) {
        const collectionGrid = cardDetails.closest('.cz-collection-grid');
        collectionGrid?.querySelectorAll('[data-cz-card-details][open]').forEach((otherDetails) => {
          if (otherDetails !== cardDetails) closeCardDetails(otherDetails);
        });
        if (cardDetailsAreMobile()) {
          window.setTimeout(() => cardDetails.querySelector('.cz-product-card__drawer-head [data-cz-card-details-close]')?.focus(), 30);
        }
      }
      syncCardDetailsLock();
      return;
    }

    const details = event.target.closest?.('.cz-mobile-filters');
    if (!details) return;
    syncFilterLock();
    if (details.open) window.setTimeout(() => details.querySelector('[data-cz-filter-close]')?.focus(), 30);
  }, true);

  document.addEventListener('change', (event) => {
    const sort = event.target.closest('[data-cz-sort-select]');
    if (sort) {
      sort.form?.submit();
      return;
    }

    const variantSelect = event.target.closest('[data-cz-variant-select]');
    if (variantSelect) {
      updateProductVariant(variantSelect);
      return;
    }

    const quantity = event.target.closest('[data-cz-quantity]');
    if (quantity) syncMultibuy(quantity.closest('[data-cz-product-root]'), quantity.value);
  });

  document.addEventListener('click', (event) => {
    const cardDetailsClose = event.target.closest('[data-cz-card-details-close]');
    if (cardDetailsClose) {
      closeCardDetails(cardDetailsClose.closest('[data-cz-card-details]'), true);
      return;
    }

    const filterClose = event.target.closest('[data-cz-filter-close]');
    if (filterClose) {
      closeFilters(filterClose.closest('.cz-mobile-filters'));
      return;
    }

    const drawer = event.target.closest('.cz-mobile-filter-drawer');
    if (drawer && event.target === drawer) {
      closeFilters(drawer.closest('.cz-mobile-filters'));
      return;
    }

    const multibuyTier = event.target.closest('[data-cz-multibuy-tier]');
    if (multibuyTier) {
      const root = multibuyTier.closest('[data-cz-product-root]');
      syncMultibuy(root, multibuyTier.dataset.quantity);
      return;
    }

    const minus = event.target.closest('[data-cz-qty-minus]');
    const plus = event.target.closest('[data-cz-qty-plus]');
    if (minus || plus) {
      const quantity = (minus || plus).closest('.cz-quantity')?.querySelector('[data-cz-quantity]');
      if (!quantity) return;
      const current = Math.max(1, asNumber(quantity.value));
      quantity.value = minus ? Math.max(1, current - 1) : current + 1;
      syncMultibuy(quantity.closest('[data-cz-product-root]'), quantity.value);
    }
  });

  document.addEventListener('keydown', (event) => {
    const openCardDetails = document.querySelector('[data-cz-card-details][open]');

    if (event.key === 'Tab' && openCardDetails && cardDetailsAreMobile()) {
      const drawer = openCardDetails.querySelector('.cz-product-card__more-body');
      const focusable = Array.from(drawer?.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])') || [])
        .filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0);
      if (focusable.length) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
      return;
    }

    if (event.key !== 'Escape') return;
    if (openCardDetails) closeCardDetails(openCardDetails, true);
    document.querySelectorAll('.cz-mobile-filters[open]').forEach(closeFilters);
  });

  window.addEventListener('resize', () => {
    if (window.innerWidth > 920) document.querySelectorAll('.cz-mobile-filters[open]').forEach(closeFilters);
    syncCardDetailsLock();
  });

  const initialize = (scope = document) => {
    setupProductGalleries(scope);
    setupStickyBuy(scope);
    setupMultibuy(scope);
    syncCardDetailsLock();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
